// Cloudflare Turnstile for the admin sign-in: the same widget as the user site's
// (apps/web/src/components/Turnstile.tsx); keep the two in step. Only tokens solved on the admin
// host count here (apps/api/src/lib/turnstile.ts).
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id: string) => void;
    };
  }
}

let loader: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  loader ??= new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('turnstile failed to load'));
    document.head.appendChild(s);
  }).catch((e: unknown) => {
    // Forget a failed load so "Try again" can fetch the script afresh.
    loader = null;
    throw e;
  });
  return loader;
}

/** The widget's own size: "flexible" fills the width (300px minimum) at a fixed 65px height. */
const W = 300;
const H = 65;

/**
 * Cloudflare Turnstile widget. Renders nothing when no site key is configured. Its 65px are
 * reserved from the first frame (a quiet placeholder sits underneath until the widget paints over
 * it), so the form never jumps. It spans the form's width, and on the narrowest phones, where the
 * form is under the widget's 300px minimum, it is scaled down to fit instead of poking out.
 */
export function Turnstile({
  siteKey,
  onToken,
  onUnavailable,
}: {
  siteKey: string | null | undefined;
  onToken: (t: string | undefined) => void;
  /**
   * The widget couldn't load or errored (e.g. this host isn't on the widget's hostname list). The
   * form then lets staff try anyway and the API decides, so a widget problem can't lock the team out
   * while the check isn't enforced.
   */
  onUnavailable?: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const mount = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [siteKey]);

  useEffect(() => {
    if (!siteKey || !mount.current) return;
    let id: string | undefined;
    let cancelled = false;
    setFailed(false);
    loadScript()
      .then(() => {
        if (cancelled || !mount.current || !window.turnstile) return;
        id = window.turnstile.render(mount.current, {
          sitekey: siteKey,
          theme: 'light',
          size: 'flexible',
          callback: (t: string) => onToken(t),
          'expired-callback': () => onToken(undefined),
          'error-callback': () => {
            onToken(undefined);
            onUnavailable?.();
          },
        });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
        onToken(undefined);
        onUnavailable?.();
      });
    return () => {
      cancelled = true;
      if (id && window.turnstile) window.turnstile.remove(id);
    };
  }, [siteKey, onToken, onUnavailable, attempt]);

  if (!siteKey) return null;
  const scale = width > 0 && width < W ? width / W : 1;
  return (
    <div ref={box} className="relative" style={{ height: Math.ceil(H * scale) }}>
      <div
        role={failed ? 'alert' : undefined}
        className="absolute inset-0 flex items-center gap-3 rounded-[6px] border border-hairline bg-paper px-3.5 text-[13px] leading-snug text-stone"
      >
        {failed ? (
          <>
            <span className="min-w-0 flex-1">Couldn't load the human check.</span>
            <button
              type="button"
              onClick={() => setAttempt((n) => n + 1)}
              className="-mr-1.5 h-11 shrink-0 rounded-[8px] px-2.5 font-semibold text-ink underline underline-offset-2"
            >
              Try again
            </button>
          </>
        ) : (
          <>
            <span
              aria-hidden="true"
              className="h-4 w-4 shrink-0 animate-[press-spin_0.8s_linear_infinite] rounded-full border-2 border-hairline border-t-stone"
            />
            Checking you're human…
          </>
        )}
      </div>
      <div
        ref={mount}
        className="relative"
        style={
          scale < 1 ? { width: W, transform: `scale(${scale})`, transformOrigin: '0 0' } : undefined
        }
      />
    </div>
  );
}
